<template>
  <div class="goal-container">
    <callout
      class="goal-list"
      :controlled="true"
      :has-border="false"
      has-gradient
      :has-toggle="hasToggle"
      :opened="isCalloutOpen"
      @click:open="() => (isCalloutOpen = !isCalloutOpen)"
    >
      <div class="row align-items-center">
        <div
          class="col-12 col-md-10 pl-0 py-2"
          :class="{ 'pl-3 pl-md-0': goal.objectives.length == 0 }"
        >
          <span class="gray-chip">Meta #{{ position }}</span>
          <p class="text-format mt-2 pl-1">{{ goal.description }}</p>
        </div>
        <div
          v-if="!readOnly"
          class="col-12 col-md-2 pl-0 ml-md-n1 text-right"
          :class="goal.objectives.length != 0 ? 'ml-n3' : 'ml-1'"
        >
          <drop-down class="goals-action" :items="actionItems" />
        </div>
      </div>
      <template #edition>
        <div v-if="showAreaSelection" class="col-12 col-md-10">
          <SelectedGoalsAreas :readonly="true" :selected-goals="goal" />
        </div>
        <intervention-plan-objetives
          :achievement-summary="achievementSummary"
          :add-objetive="addObjetive"
          :data="goal.objectives"
          :goal-number="position"
          :legend-data="legendData"
          :measurement="measureCodes"
          :read-only="readOnly"
          :submitted-summaries="submittedSummaries"
          :summary-read-only="summaryReadOnly"
          @cancel-add="cancelObjHandler"
          @delete-objetive="deleteObjetiveHandler"
          @edit-objetive="editObjetiveHandler"
          @save-objetive="saveObjetiveHandler"
        />
      </template>
    </callout>
    <div v-if="!readOnly" class="d-flex justify-content-end">
      <action-button
        i="fas fa-file-plus"
        responsive
        text="Añadir Objetivo"
        variant="success"
        @click="addObjetives()"
      />
    </div>
  </div>
</template>

<script>
import DropDown from 'dropdown';
import ActionButton from 'ActionButton';
import Callout from 'custom-callout';
import InterventionPlanObjetives from '@/views/provider/referral-management/search/components/InterventionPlanObjetives.vue';
import { getMeasureCodes } from '@/modules/referralManagement/api';
import alertMixin from 'utils/mixins/alert.mixin';
import SelectedGoalsAreas from '@/views/provider/referral-management/search/components/SelectedGoalsAreas.vue';

export default {
  name: 'InterventionPlanGoals',
  components: {
    DropDown,
    Callout,
    InterventionPlanObjetives,
    ActionButton,
    SelectedGoalsAreas,
  },
  mixins: [alertMixin],
  props: {
    achievementSummary: {
      type: Boolean,
      default: () => false,
    },
    readOnly: {
      type: Boolean,
      default: () => false,
    },
    summaryReadOnly: {
      type: Boolean,
      default: () => false,
    },
    legendData: {
      type: Object,
      default: () => {},
    },
    addGoal: {
      type: Boolean,
      default: () => false,
    },
    data: {
      type: Object,
      default: () => {},
    },
    position: {
      type: Number,
      default: () => 0,
    },
    submittedSummaries: {
      type: Array,
      default: () => [],
    },
    showAreaSelection: {
      type: Boolean,
      default: false,
    },
  },
  data: () => ({
    goal: {
      description: null,
      objectives: [],
      areaSelection: [],
      goalSelection: [],
    },
    add: false,
    addObjetive: false,
    isCalloutOpen: false,
    measureCodes: [],
  }),
  computed: {
    actionItems() {
      return [
        {
          name: 'edit',
          label: 'Editar',
          icon: 'fas fa-edit color-primary-1',
          border: 'bottom',
          click: () => this.editGoal(),
        },
        {
          name: 'delete',
          label: 'Eliminar',
          icon: 'fas fa-times-circle color-primary-3',
          click: () => this.deleteGoal(),
        },
      ];
    },
    hasToggle() {
      return (
        this.goal.objectives?.length != 0 ||
        this.goal.goalSelection?.length != 0
      );
    },
  },
  watch: {
    data: {
      immediate: true,
      handler(current) {
        if (current) {
          this.goal = current;
        }
      },
    },
  },
  async mounted() {
    await this.getMeasures();
  },
  methods: {
    async getMeasures() {
      const { data } = await getMeasureCodes();
      this.measureCodes = data;
    },
    showAddGoals() {
      this.add = true;
    },
    async deleteGoal() {
      const answer = await this.ShowCancelModal(
        'Alerta',
        '¿Está seguro que desea eliminar la meta?',
        'Sí',
        'No'
      );

      if (!answer) return;

      this.$emit('delete-goal', this.goal);
      this.ShowToast(
        'Alerta',
        'La meta ha sido eliminada exitosamente.',
        'success'
      );
    },
    async addObjetives() {
      this.addObjetive = true;
      this.isCalloutOpen = true;
    },
    saveObjetiveHandler(objetive) {
      const obj = {
        id: Math.random() * 1000,
        ...objetive,
      };
      this.goal.objectives.push(obj);
      this.cancelObjHandler();
    },
    editObjetiveHandler(objetive) {
      const index = this.goal.objectives.findIndex((o) => o.id == objetive.id);
      this.goal.objectives[index] = objetive;
      this.$emit('on-change-data', this.goal);
    },
    async deleteObjetiveHandler(id) {
      const answer = await this.ShowCancelModal(
        'Alerta',
        '¿Está seguro que desea eliminar el objetivo?',
        'Sí',
        'No'
      );

      if (!answer) return;
      const index = this.goal.objectives.findIndex((o) => o.id == id);
      this.goal.objectives.splice(index, 1);
      this.ShowToast(
        'Alerta',
        'El objetivo ha sido eliminado exitosamente.',
        'success'
      );
      this.isCalloutOpen = this.goal.objectives.length ? true : false;
    },
    cancelObjHandler() {
      this.isCalloutOpen = this.goal.objectives.length ? true : false;
      this.addObjetive = false;
    },
    editGoal() {
      this.$emit('edit-goal');
    },
  },
};
</script>

<style lang="scss" scoped>
@import '~@/shared/scss/config/colors.scss';
.goal-container {
  .text-format {
    overflow-wrap: break-word;
    text-align: justify;
  }
  .goal-list ::v-deep {
    margin: 1rem 0;
    .border--top {
      border-top-width: 0px;
    }
    .gradient-toggle-mob {
      display: none;
    }
    .gradient-toggle {
      display: flex !important;
      box-shadow: none;
      em {
        padding-left: 0.2rem;
        padding-bottom: 3rem;
        &.fa-chevron-circle-right {
          color: map-get($colors, 'primary-1') !important;
        }
      }
    }

    .bg-secondary-3 {
      background-color: map-get($colors, 'white') !important;
    }
    @media only screen and (max-width: 768px) {
      .goals-action {
        .btn {
          width: 100%;
        }
      }
      .gradient-toggle {
        em {
          padding-bottom: 3rem;
        }
      }
    }
  }
  .gray-chip {
    background-color: map-get($colors, 'gray-2');
    border-radius: 0.9rem;
    color: white;
    padding: 0.2rem 0.5rem;
  }
}
</style>
